import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import MasterLayout from "../../_metronic/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { MenuTestPage } from "../pages/MenuTestPage";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import Dashboard from "app/pages/dashboard/DashboardPage";
import { CPLModule } from "app/modules/shared/cplModules/CPLModule";
import { CPLModules } from "cpl-react-common/components/Enums/CPLModules";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { PermittedRoute } from "services/userSession/UserPermittedComponent";
import UserGroupPage from "app/modules/admin/userGroups/UserGroupPage";
import PermissionsAdminPage from "app/modules/admin/userPermissions/PermissionsAdminPage";
import CompanyBrandingPage from "app/modules/admin/companyBranding/CompanyBrandingPage";
import UserTablePage from "app/modules/admin/userTable/UserTablePage";
import NewUserProfile from "app/modules/admin/userProfile/components/NewUserProfile";
import BackgroundActivitiesPage from "app/modules/backgroundActivities/BackgroundActivitiesPage";
import NewMeetingForm from "app/modules/scheduling/meetings/components/NewMeetingForm";
import ServiceChargeInvoicingPage from "app/modules/serviceChargeInvoicing/ServiceChargeInvoicingPage";
import AnticipatedExpenditurePage from "app/modules/anticipatedExpenditure/AnticipatedExpenditurePage";
import ServiceChargeInvoicingWizard from "app/modules/serviceChargeInvoicing/components/ServiceChargeInvoiceWizard";
import ServiceChargeInvoiceDetailsPage from "app/modules/serviceChargeInvoicing/ServiceChargeInvoiceDetailsPage";
import AIAssistantPage from "app/modules/aiAssistant/page/AIAssistantPage";

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import("app/modules/admin/userProfile/ProfilePage"));
  const ComponentsTestPage = lazy(() => import("app/modules/cpltest/componentsTest/ComponentsTestPage"));
  const UserTasksTablePage = lazy(() => import("app/modules/scheduling/userTasks/UserTasksTablePage"));
  const PowerBIPage = lazy(() => import("app/modules/powerbi/PowerBIPage"));
  const PaymentProviderRoutes = lazy(() => import("app/modules/admin/paymentProviders/PaymentProviderRoutes"));
  const TermsAndConditionsPage = lazy(() => import("app/modules/admin/termsAndConditions/TermsAndConditionsPage"));
  const CustomisableTypesRoutes = lazy(() => import("app/modules/admin/customisableTypes/CustomisableTypesRoutes"));
  const WorkingHoursRoutes = lazy(() => import("app/modules/admin/workingHours/WorkingHoursRoutes"));
  const CaseRoutes = lazy(() => import("app/modules/cases/CaseRoutes"));
  const CalendarPage = lazy(() => import("app/modules/scheduling/calendar/CalendarPage"));
  const MeetingsTablePage = lazy(() => import("app/modules/scheduling/meetings/MeetingsTablePage"));
  const CasesTablePage = lazy(() => import("app/modules/cases/CasesTablePage"));
  const CaseForm = lazy(() => import("app/modules/cases/components/CaseForm"));
  const CalendarEventChangeTracking = lazy(
    () => import("app/modules/scheduling/calendar/components/CalendarEventChangeTracking")
  );
  const MeetingRoutes = lazy(() => import("app/modules/scheduling/meetings/MeetingRoutes"));
  const UserTasksRoutes = lazy(() => import("app/modules/scheduling/userTasks/UserTasksRoutes"));
  const UserTask = lazy(() => import("app/modules/scheduling/userTasks/components/UserTask"));
  const ClientPortalUsersTablePage = lazy(
    () => import("app/modules/clients/clientsPortalUsers/ClientsPortalUserTablePage")
  );
  const ClientPortalUserRoutes = lazy(() => import("app/modules/clients/clientsPortalUsers/ClientPortalUserRoutes"));
  const NewDirectorPage = lazy(() => import("app/modules/directors/NewDirectorPage"));
  const DirectorRoutes = lazy(() => import("app/modules/directors/DirectorRoutes"));
  const JobsTablePage = lazy(() => import("app/modules/jobs/JobsTablePage"));
  const JobRoutes = lazy(() => import("app/modules/jobs/JobRoutes"));
  const JobForm = lazy(() => import("app/modules/jobs/components/JobForm"));
  const EstimatesTablePage = lazy(() => import("app/modules/estimates/EstimatesTablePage"));
  const EstimateForm = lazy(() => import("app/modules/estimates/components/EstimateForm"));
  const EstimateRoutes = lazy(() => import("app/modules/estimates/EstimateRoutes"));
  const WebNarrativesRoutes = lazy(() => import("app/modules/admin/webNarratives/WebNarrativesRoutes"));
  const CPLSettingsRoutes = lazy(() => import("app/modules/admin/cplSettings/CPLSettingsRoutes"));
  const DirectDebitCollectionDaysPage = lazy(
    () => import("app/modules/admin/directDebitCollectionDays/DirectDebitCollectionDaysPage")
  );
  const DataImportRoutes = lazy(() => import("app/modules/dataImport/DataImportRoutes"));
  const DirectorsTablePage = lazy(() => import("app/modules/directors/DirectorsTablePage"));

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />
        <Route path="AccountLink/*" element={<Navigate to="/dashboard" />} />
        {/* Pages */}
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="menu-test" element={<MenuTestPage />} />
        <Route
          path="Admin/Users/Users/*"
          element={
            <PermittedRoute permission={Permissions.ViewUsers}>
              <UserTablePage />
            </PermittedRoute>
          }
        />
        <Route
          path="Admin/Users/UserGroups/*"
          element={
            <PermittedRoute permission={Permissions.ViewUserGroups}>
              <UserGroupPage />
            </PermittedRoute>
          }
        />
        <Route
          path="Admin/Users/Permissions/*"
          element={
            <PermittedRoute permission={Permissions.ViewPermissions}>
              <PermissionsAdminPage />
            </PermittedRoute>
          }
        />
        <Route
          path="Admin/CompanyBranding/*"
          element={
            <CPLModule module={CPLModules.ClientPortal} showErrorPage>
              <PermittedRoute permission={Permissions.ViewCompanyBranding}>
                <CompanyBrandingPage />
              </PermittedRoute>
            </CPLModule>
          }
        />
        <Route
          path="Admin/WebNarratives/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.EditWebNarratives}>
                  <WebNarrativesRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/CPLSettings/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal}>
                <PermittedRoute permission={Permissions.ManageSettings}>
                  <CPLSettingsRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/Users/NewUser/"
          element={
            <PermittedRoute permission={Permissions.AddUsers}>
              <NewUserProfile />
            </PermittedRoute>
          }
        />
        {/* Lazy Modules */}
        <Route
          path="Admin/WorkingHours/*"
          element={
            <SuspensedView>
              <PermittedRoute permission={Permissions.ManageWorkingHours}>
                <WorkingHoursRoutes />
              </PermittedRoute>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/CustomisableTypes/*"
          element={
            <SuspensedView>
              <PermittedRoute permission={Permissions.ManageCustomisableTypes}>
                <CustomisableTypesRoutes />
              </PermittedRoute>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/PaymentProviders/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.ViewPaymentSettings}>
                  <PaymentProviderRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/DirectDebitCollectionDays/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.ViewPaymentSettings}>
                  <DirectDebitCollectionDaysPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/TermsAndConditions/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.ViewTermsAndConditionsEditor}>
                  <TermsAndConditionsPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Admin/UserProfile/:userID"
          element={
            <SuspensedView>
              <PermittedRoute permission={Permissions.ViewUsers}>
                <ProfilePage />
              </PermittedRoute>
            </SuspensedView>
          }
        />
        <Route
          path="Profile/*"
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path="CPLTest/*"
          element={
            <SuspensedView>
              <ComponentsTestPage />
            </SuspensedView>
          }
        />
        <Route
          path="Calendar/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <PermittedRoute permission={Permissions.ViewAllCalendarEvents}>
                  <CalendarPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Meetings/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <PermittedRoute permission={Permissions.ViewAllMeetings}>
                  <MeetingsTablePage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Cases/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Cases} showErrorPage>
                <PermittedRoute permission={Permissions.ViewAllCases}>
                  <CasesTablePage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Case/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Cases} showErrorPage>
                <PermittedRoute permission={Permissions.CreateCases}>
                  <CaseForm />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Case/:caseID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Cases} showErrorPage>
                <CaseRoutes />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Calendar/History/:eventID"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <PermittedRoute permission={Permissions.ViewAllCalendarEvents}>
                  <CalendarEventChangeTracking />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Meeting/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <PermittedRoute permission={Permissions.CreateMeetings}>
                  <NewMeetingForm />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Meeting/:meetingID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <MeetingRoutes />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="UserTasks/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <UserTasksTablePage />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="UserTask/:userTaskID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <UserTasksRoutes />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="UserTask/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Scheduling} showErrorPage>
                <UserTask />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="ResidentsPortalUsers/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.ViewClientPortalUsers}>
                  <ClientPortalUsersTablePage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="ResidentsPortalUser/:clientPortalUserID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ClientPortal} showErrorPage>
                <PermittedRoute permission={Permissions.ViewClientPortalUsers}>
                  <ClientPortalUserRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Jobs/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Jobs} showErrorPage>
                <JobsTablePage />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Job/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Jobs} showErrorPage>
                <JobForm />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Job/:jobID/:taskSeqID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Jobs} showErrorPage>
                <JobRoutes />
              </CPLModule>
            </SuspensedView>
          }
        />{" "}
        <Route
          path="Estimates/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Estimates} showErrorPage>
                <EstimatesTablePage />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Estimate/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Estimates} showErrorPage>
                <EstimateForm />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Estimate/:estimateID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.Estimates} showErrorPage>
                <EstimateRoutes />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="ServiceChargeInvoicing/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ROUK} showErrorPage>
                <PermittedRoute permission={Permissions.ViewServiceChargeInvoiceSummaries}>
                  <ServiceChargeInvoicingPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="ServiceChargeInvoice/:logIDParam/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ROUK} showErrorPage>
                <PermittedRoute permission={Permissions.ViewServiceChargeInvoiceDetails}>
                  <ServiceChargeInvoiceDetailsPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="ServiceChargeInvoicing/Wizard/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ROUK} showErrorPage>
                <PermittedRoute permission={Permissions.GenerateServiceChargeInvoice}>
                  <ServiceChargeInvoicingWizard />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="AnticipatedExpenditure/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.ROUK} showErrorPage>
                <PermittedRoute permission={Permissions.GenerateAnticipatedExpenditureReport}>
                  <AnticipatedExpenditurePage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="PowerBI"
          element={
            <SuspensedView>
              <PermittedRoute permission={Permissions.ViewPowerBIReports}>
                <PowerBIPage />
              </PermittedRoute>
            </SuspensedView>
          }
        />
        <Route
          path="assistimo/:chatSessionID?"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.AIAssistant} showErrorPage>
                <AIAssistantPage />
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="BackgroundActivities"
          element={
            <SuspensedView>
              <BackgroundActivitiesPage />
            </SuspensedView>
          }
        />
        <Route
          path="DataImport/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.DataImport} showErrorPage>
                <PermittedRoute permission={Permissions.AccessSelfServiceDataImport}>
                  <DataImportRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Directors"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.InDevelopment} showErrorPage>
                <PermittedRoute permission={Permissions.ViewDirectors}>
                  <DirectorsTablePage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Director/"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.InDevelopment} showErrorPage>
                <PermittedRoute permission={Permissions.CreateDirectors}>
                  <NewDirectorPage />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        <Route
          path="Director/:directorID/*"
          element={
            <SuspensedView>
              <CPLModule module={CPLModules.InDevelopment} showErrorPage>
                <PermittedRoute permission={Permissions.ViewDirectors}>
                  <DirectorRoutes />
                </PermittedRoute>
              </CPLModule>
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--kt-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes, SuspensedView };
